<template>
  <span class="check-toggle">
    <span
      v-if="label"
      class="check-toggle__label"
    >{{ label }}</span>

    <b-button
      type="button"
      variant="link"
      size="sm"
      class="check-toggle__button"
      :class="{ 'check-toggle__button--final': checked && !uncheck }"
      :title="checked ? checkedTitle : checkTitle"
      :aria-label="checked ? checkedAriaLabel : checkTitle"
      :disabled="isButtonDisabled"
      :data-cy="checked ? uncheckDataCy : checkDataCy"
      @click="onClick"
    >
      <CheckCircleMark :checked="checked" />
    </b-button>

    <!-- Lazy: a table renders one toggle per row, and only the one being unchecked needs its modal -->
    <b-modal
      v-if="uncheck"
      :id="uncheckModalId"
      v-model="isUncheckModalVisible"
      lazy
      header-class="custom-modal"
      body-class="custom-modal"
      footer-class="custom-modal"
      :title="uncheckModalTitle"
      button-size="sm"
    >
      <p
        class="mb-2"
        data-cy="uncheck-modal-text"
      >
        {{ uncheckModalText }}
      </p>

      <template #modal-footer>
        <b-button
          type="button"
          variant="secondary"
          size="sm"
          :disabled="isUnchecking"
          data-cy="cancel-uncheck"
          @click="isUncheckModalVisible = false"
        >
          Cancel
        </b-button>

        <BaseButtonLoader
          :loading="isUnchecking"
          class="ml-2"
          type="button"
          :custom-button="false"
          size="sm"
          variant="danger"
          data-cy="confirm-uncheck"
          @click="confirmUncheck"
        >
          {{ uncheckConfirmText }}
        </BaseButtonLoader>
      </template>
    </b-modal>
  </span>
</template>

<script>
import CheckCircleMark from '@/components/common/CheckCircleMark.vue'

/**
 * A yes/no mark on one item as a circle button, e.g. Sale or Qualified on a call: the empty circle
 * checks it, the green check unchecks it after a confirmation. What checking and unchecking do is the
 * parent's: it passes `check` / `uncheck` as async functions, and the toggle guards against double
 * clicks while one runs. Without `uncheck` a check is final and the green check is not clickable.
 * Every visible text (label, tooltips, modal) is a prop, so each use names its own mark.
 *
 * `uncheck` must reject on failure (after telling the user why), so the modal stays open for another try.
 */
export default {
  name: 'CheckToggle',

  components: {
    CheckCircleMark
  },

  props: {
    checked: {
      type: Boolean,
      default: false
    },

    check: {
      type: Function,
      required: true
    },

    uncheck: {
      type: Function,
      default: null
    },

    // Shown before the circle, e.g. "Sale?"; none when empty
    label: {
      type: String,
      default: ''
    },

    // Busy elsewhere (e.g. a check sent from another tab)
    disabled: {
      type: Boolean,
      default: false
    },

    checkTitle: {
      type: String,
      default: 'Mark'
    },

    checkedTitle: {
      type: String,
      default: 'Marked'
    },

    checkedAriaLabel: {
      type: String,
      default: 'Marked'
    },

    uncheckModalTitle: {
      type: String,
      default: 'Undo'
    },

    uncheckModalText: {
      type: String,
      default: 'Undo this?'
    },

    uncheckConfirmText: {
      type: String,
      default: 'Undo'
    },

    checkDataCy: {
      type: String,
      default: 'check-toggle-check'
    },

    uncheckDataCy: {
      type: String,
      default: 'check-toggle-uncheck'
    }
  },

  data() {
    return {
      isChecking: false,
      isUnchecking: false,
      isUncheckModalVisible: false
    }
  },

  computed: {
    isButtonDisabled() {
      return this.disabled || this.isChecking || this.isUnchecking || (this.checked && !this.uncheck)
    },

    uncheckModalId() {
      return `check-toggle-uncheck-modal-${this._uid}`
    }
  },

  methods: {
    async onClick() {
      if (this.isButtonDisabled) {
        return
      }

      if (this.checked) {
        this.isUncheckModalVisible = true
        return
      }

      this.isChecking = true

      try {
        await this.check()
      }
      finally {
        this.isChecking = false
      }
    },

    async confirmUncheck() {
      if (this.isUnchecking) {
        return
      }

      this.isUnchecking = true

      try {
        await this.uncheck()

        this.isUncheckModalVisible = false
      }
      catch (error) {
        // Already told; the modal stays open for another try
      }
      finally {
        this.isUnchecking = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.check-toggle {
  display: inline-flex;
  align-items: center;
}

.check-toggle__label {
  margin-right: 0.625rem;
}

// Keeps the btn-sm box, so the circle lines up with the small buttons around it (Call Logs' Play)
.check-toggle__button {
  padding-left: 0;
  padding-right: 0;
  line-height: $btn-line-height-sm;

  // The ticked mark's teal (CheckCircleMark)
  &:not(:disabled):hover .check-circle-mark,
  &:not(:disabled):focus .check-circle-mark {
    border-color: #4fb59a;
  }
}

// A check that can't be undone here reads at full strength rather than as a faded, disabled control
.check-toggle__button--final:disabled {
  opacity: 1;
}
</style>
